import { inject, Injectable } from '@angular/core';

import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ShApiCachingHelper } from '../../core/api/helpers/api-caching.helper';
import { ShAbstractHttpService } from '../../core/http/services/http.abstract.service';
import { ShPaginationService } from '../../core/http/services/pagination.service';
import { cacheValueWhileSubscribed } from '../../helpers/typed-reactive-filters.helper';
import { MetabaseReport } from '../../metabase-reports/entities/report.entity';
import { MediaHavenPlugin, PluginObject } from '../../modules/entities/mediahaven-plugins.entity';
import { ShModulesService } from '../../modules/services/modules.service';
import { PaginatedResults } from '../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { mapToPaginatedResult } from '../../overviews/generic-overview/components/pagination/helpers/map-to-paginated-result.helper';

@Injectable()
export abstract class ShReportService {
    protected abstract readonly ENDPOINT: string;
    protected defaultCollection$: Observable<string | undefined>;
    protected defaultDashboard$: Observable<number | undefined>;

    // Injectors
    private readonly paginationService: ShPaginationService = inject(ShPaginationService);
    private readonly http: ShAbstractHttpService = inject(ShAbstractHttpService);
    private readonly moduleService: ShModulesService = inject(ShModulesService);
    private readonly apiCachingHelper: ShApiCachingHelper = inject(ShApiCachingHelper);

    constructor() {
        this.initDefaults();
    }

    protected initDefaults(): void {
        const plugin$: Observable<PluginObject> = this.moduleService
            .getPlugin(MediaHavenPlugin.METABASE)
            .pipe(cacheValueWhileSubscribed());

        this.defaultCollection$ = plugin$.pipe(
            map(plugin => plugin.CustomProperties?.default_collection as string | undefined),
            shareReplay(1)
        );

        this.defaultDashboard$ = plugin$.pipe(
            map(plugin => plugin?.CustomProperties?.default_dashboard as number | undefined),
            shareReplay(1)
        );
    }

    private fetchReport(id: string): Observable<MetabaseReport> {
        return this.http.get<MetabaseReport>(`${this.ENDPOINT}/${id}`);
    }

    getAll(paginationInfo: PaginationInfo): Observable<PaginatedResults<MetabaseReport>> {
        return this.defaultCollection$.pipe(
            take(1),
            map(collection => (collection ? new HttpParams().append('collection', collection) : new HttpParams())),
            switchMap(params =>
                this.paginationService.get<MetabaseReport>(
                    this.ENDPOINT,
                    params,
                    undefined,
                    paginationInfo.page,
                    paginationInfo.resultsPerPage
                )
            ),
            map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage))
        );
    }

    get(id: string): Observable<MetabaseReport> {
        return this.fetchReport(id);
    }

    getDefaultDashboardId(): Observable<number | undefined> {
        return this.defaultDashboard$;
    }
}
